import type { Prng } from './prng.ts';

/**
 * Fisher–Yates, in place on a copy.
 *
 * Written out rather than reached for from a library because the exact order of draws is part of
 * the reproducibility contract: any change here changes every future session generated from an old
 * seed, which is why `blueprint.algorithm` is versioned alongside it.
 */
export function shuffle<T>(items: readonly T[], prng: Prng): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = prng.nextInt(index + 1);
    // Both indices are inside the array by construction of the loop.
    const swap = result[index] as T;
    result[index] = result[target] as T;
    result[target] = swap;
  }
  return result;
}

export interface Anchored {
  readonly fixedPosition: boolean;
}

/**
 * Shuffles while leaving anchored entries where they were authored.
 *
 * "None of the above" stops meaning anything once it is in the middle of the list, and "All of the
 * above" becomes actively wrong. Anchoring is the content's way of saying so, and the shuffle has
 * to respect it or the option is unusable with randomisation on.
 */
export function shuffleAnchored<T extends Anchored>(items: readonly T[], prng: Prng): T[] {
  const movable = items.filter((item) => !item.fixedPosition);
  if (movable.length === items.length) {
    return shuffle(items, prng);
  }

  const shuffled = shuffle(movable, prng);
  let cursor = 0;

  // One shuffled entry per movable item, so the cursor cannot outrun the array.
  return items.map((item) => {
    if (item.fixedPosition) {
      return item;
    }
    const next = shuffled[cursor];
    cursor += 1;
    return next ?? item;
  });
}
